import * as React from "react"
import { motion, AnimatePresence } from "motion/react"
import {
  Spade,
  Plus,
  Users,
  Coins,
  Bot,
  Activity,
  BarChart3,
  Pencil,
  Trash2,
  Loader2,
  X,
  RefreshCw,
  MinusCircle,
  ToggleLeft,
  ToggleRight,
  ChevronDown,
} from "lucide-react"
import { useAuth } from "@/contexts/AuthContext"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

/* ─── Types ─── */

type Section = "rooms" | "bots" | "rules" | "monitoring" | "analytics"

interface PokerRoom {
  _id: string
  name: string
  status: "waiting" | "playing" | "active" | "paused" | "closed" | "ended"
  maxPlayers: number
  players: Array<{
    displayName?: string
    seatIndex: number
    isBot?: boolean
    difficulty?: "easy" | "medium" | "hard"
    chips?: number
  }>
  buyIn: number
  smallBlind: number
  bigBlind: number
  startingChips: number
  blindIncreaseInterval: number
  handTimeLimit: number
  botConfig: {
    enabled: boolean
    difficulty: "easy" | "medium" | "hard"
    maxBots: number
    autoFill: boolean
    autoFillTimeout: number
  }
  createdAt: string
  updatedAt: string
}



interface RoomFormData {
  name: string
  buyIn: number
  smallBlind: number
  bigBlind: number
  maxPlayers: number
  startingChips: number
  blindIncreaseInterval: number
  handTimeLimit: number
}

const defaultFormData: RoomFormData = {
  name: "",
  buyIn: 100,
  smallBlind: 1,
  bigBlind: 2,
  maxPlayers: 6,
  startingChips: 1000,
  blindIncreaseInterval: 15,
  handTimeLimit: 30,
}

/* ─── Section Navigation ─── */

const sections: Array<{ id: Section; label: string; icon: React.ElementType }> = [
  { id: "rooms", label: "Room Management", icon: Spade },
  { id: "bots", label: "Bot System", icon: Bot },
  { id: "rules", label: "Game Rules", icon: Coins },
  { id: "monitoring", label: "Monitoring", icon: Activity },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
]

/* ─── Status Badge Component ─── */

const statusColors: Record<string, { bg: string; text: string; border: string }> = {
  active: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/30" },
  playing: { bg: "bg-green-500/15", text: "text-green-400", border: "border-green-500/30" },
  waiting: { bg: "bg-yellow-500/15", text: "text-yellow-400", border: "border-yellow-500/30" },
  paused: { bg: "bg-gray-500/15", text: "text-gray-400", border: "border-gray-500/30" },
  closed: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
  ended: { bg: "bg-red-500/15", text: "text-red-400", border: "border-red-500/30" },
}

function StatusBadge({ status }: { status: string }) {
  const colors = statusColors[status] ?? statusColors.waiting
  if (!colors) return null
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${colors.bg} ${colors.text} ${colors.border}`}
      style={{ fontFamily: "Outfit, sans-serif" }}
    >
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

/* ─── Main Component ─── */

export function PokerRoomTab() {
  const [activeSection, setActiveSection] = React.useState<Section>("rooms")

  return (
    <div className="space-y-6">
      {/* Section Navigation */}
      <div className="flex flex-wrap gap-2">
        {sections.map((section) => {
          const isActive = activeSection === section.id
          return (
            <button
              key={section.id}
              onClick={() => setActiveSection(section.id)}
              className={`
                relative flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${isActive
                  ? "bg-[#F37020]/15 text-[#F37020]"
                  : "bg-white/[0.04] text-white/50 hover:text-white/70 hover:bg-white/[0.06]"
                }
              `}
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              <section.icon className="w-4 h-4" />
              <span>{section.label}</span>
              {isActive && (
                <motion.div
                  layoutId="pokerActiveSection"
                  className="absolute inset-0 rounded-lg border border-[#F37020]/30"
                  transition={{ type: "spring", bounce: 0.2, duration: 0.4 }}
                />
              )}
            </button>
          )
        })}
      </div>

      {/* Section Content */}
      <motion.div
        key={activeSection}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        className="min-h-[400px]"
      >
        {activeSection === "rooms" && <RoomManagementSection />}
        {activeSection === "bots" && <BotSystemSection />}
        {activeSection === "rules" && <GameRulesSection />}
        {activeSection === "monitoring" && <MonitoringSection />}
        {activeSection === "analytics" && <AnalyticsSection />}
      </motion.div>
    </div>
  )
}

/* ─── Room Management Section ─── */

function RoomManagementSection() {
  const { token } = useAuth()
  const [rooms, setRooms] = React.useState<PokerRoom[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  
  // Modal states
  const [isCreateOpen, setIsCreateOpen] = React.useState(false)
  const [editingRoom, setEditingRoom] = React.useState<PokerRoom | null>(null)
  const [deletingRoom, setDeletingRoom] = React.useState<PokerRoom | null>(null)
  const [formData, setFormData] = React.useState<RoomFormData>(defaultFormData)
  const [saving, setSaving] = React.useState(false)
  const [deleting, setDeleting] = React.useState(false)

  // Fetch rooms
  const fetchRooms = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/admin/poker/rooms", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setRooms(data.rooms || [])
      } else {
        setError(data.message || "Failed to fetch rooms")
      }
    } catch (err) {
      setError("Network error fetching rooms")
    } finally {
      setLoading(false)
    }
  }, [token])

  React.useEffect(() => {
    fetchRooms()
  }, [fetchRooms])

  // Open create modal
  const openCreateModal = () => {
    setFormData(defaultFormData)
    setEditingRoom(null)
    setIsCreateOpen(true)
  }

  // Open edit modal
  const openEditModal = (room: PokerRoom) => {
    setFormData({
      name: room.name,
      buyIn: room.buyIn,
      smallBlind: room.smallBlind,
      bigBlind: room.bigBlind,
      maxPlayers: room.maxPlayers,
      startingChips: room.startingChips,
      blindIncreaseInterval: room.blindIncreaseInterval,
      handTimeLimit: room.handTimeLimit,
    })
    setEditingRoom(room)
    setIsCreateOpen(true)
  }

  // Close modal
  const closeModal = () => {
    setIsCreateOpen(false)
    setEditingRoom(null)
    setFormData(defaultFormData)
  }

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!token) return
    setSaving(true)
    try {
      const url = editingRoom
        ? `/api/admin/poker/rooms/${editingRoom._id}`
        : "/api/admin/poker/rooms"
      const method = editingRoom ? "PUT" : "POST"
      
      const res = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      })
      const data = await res.json()
      if (data.success) {
        closeModal()
        fetchRooms()
      } else {
        setError(data.message || "Failed to save room")
      }
    } catch (err) {
      setError("Network error saving room")
    } finally {
      setSaving(false)
    }
  }

  // Handle delete
  const handleDelete = async () => {
    if (!token || !deletingRoom) return
    setDeleting(true)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${deletingRoom._id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setDeletingRoom(null)
        fetchRooms()
      } else {
        setError(data.message || "Failed to delete room")
      }
    } catch (err) {
      setError("Network error deleting room")
    } finally {
      setDeleting(false)
    }
  }

  // Loading skeleton
  if (loading) {
    return (
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8">
        <div className="flex items-center justify-center gap-3 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span style={{ fontFamily: "Outfit, sans-serif" }}>Loading rooms...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3
          className="text-lg font-semibold text-white"
          style={{ fontFamily: "Outfit, sans-serif" }}
        >
          Poker Rooms ({rooms.length})
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchRooms()}
            className="border-[#2A2A45] text-white/60 hover:text-white hover:bg-white/5"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button
            size="sm"
            onClick={openCreateModal}
            className="bg-[#F37020] hover:bg-[#F37020]/90 text-white"
          >
            <Plus className="w-4 h-4 mr-1.5" />
            Create Room
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Desktop Table View */}
      <div className="hidden md:block">
        <div className="bg-[#111117] border border-[#2A2A45] rounded-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-[#2A2A45]">
                <th className="text-left px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Name</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Status</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Players</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Stakes</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Buy-in</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rooms.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                    No rooms created yet. Click "Create Room" to get started.
                  </td>
                </tr>
              ) : (
                rooms.map((room) => (
                  <tr key={room._id} className="border-b border-[#2A2A45] last:border-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-3 text-sm text-white font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                      {room.name}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={room.status} />
                    </td>
                    <td className="px-4 py-3 text-sm text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                      <span className="inline-flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-white/40" />
                        {room.players?.length || 0}/{room.maxPlayers}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                      ${room.smallBlind}/${room.bigBlind}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#FFD700] font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                      ${room.buyIn}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => openEditModal(room)}
                          className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10 transition-colors"
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setDeletingRoom(room)}
                          className="p-1.5 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {rooms.length === 0 ? (
          <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-6 text-center text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
            No rooms created yet. Click "Create Room" to get started.
          </div>
        ) : (
          rooms.map((room) => (
            <div
              key={room._id}
              className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4 space-y-3"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="text-sm font-medium text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                    {room.name}
                  </h4>
                  <StatusBadge status={room.status} />
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => openEditModal(room)}
                    className="p-1.5 rounded-md text-white/50 hover:text-white hover:bg-white/10"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setDeletingRoom(room)}
                    className="p-1.5 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
                <div>
                  <div className="text-white/40 text-xs">Players</div>
                  <div className="text-white/70">{room.players?.length || 0}/{room.maxPlayers}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs">Stakes</div>
                  <div className="text-white/70">${room.smallBlind}/${room.bigBlind}</div>
                </div>
                <div>
                  <div className="text-white/40 text-xs">Buy-in</div>
                  <div className="text-[#FFD700] font-medium">${room.buyIn}</div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Create/Edit Modal */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent className="bg-[#111117] border-[#2A2A45] text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {editingRoom ? "Edit Room" : "Create Room"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Room Name</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="High Stakes Table"
                required
                className="bg-[#1A1A2E] border-[#2A2A45] text-white placeholder:text-white/30"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="buyIn" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Buy-in ($)</Label>
                <Input
                  id="buyIn"
                  type="number"
                  min={1}
                  value={formData.buyIn}
                  onChange={(e) => setFormData({ ...formData, buyIn: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="maxPlayers" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Max Players</Label>
                <Input
                  id="maxPlayers"
                  type="number"
                  min={2}
                  max={6}
                  value={formData.maxPlayers}
                  onChange={(e) => setFormData({ ...formData, maxPlayers: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="smallBlind" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Small Blind ($)</Label>
                <Input
                  id="smallBlind"
                  type="number"
                  min={1}
                  value={formData.smallBlind}
                  onChange={(e) => setFormData({ ...formData, smallBlind: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="bigBlind" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Big Blind ($)</Label>
                <Input
                  id="bigBlind"
                  type="number"
                  min={1}
                  value={formData.bigBlind}
                  onChange={(e) => setFormData({ ...formData, bigBlind: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="startingChips" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Starting Chips</Label>
                <Input
                  id="startingChips"
                  type="number"
                  min={100}
                  value={formData.startingChips}
                  onChange={(e) => setFormData({ ...formData, startingChips: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="handTimeLimit" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>Hand Time (sec)</Label>
                <Input
                  id="handTimeLimit"
                  type="number"
                  min={10}
                  max={120}
                  value={formData.handTimeLimit}
                  onChange={(e) => setFormData({ ...formData, handTimeLimit: Number(e.target.value) })}
                  required
                  className="bg-[#1A1A2E] border-[#2A2A45] text-white"
                />
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={closeModal}
                className="border-[#2A2A45] text-white/60 hover:text-white hover:bg-white/5"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="bg-[#F37020] hover:bg-[#F37020]/90 text-white"
              >
                {saving && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
                {editingRoom ? "Save Changes" : "Create Room"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deletingRoom} onOpenChange={() => setDeletingRoom(null)}>
        <DialogContent className="bg-[#111117] border-[#2A2A45] text-white max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>Delete Room</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-white/60" style={{ fontFamily: "Outfit, sans-serif" }}>
            Are you sure you want to delete "{deletingRoom?.name}"? This action cannot be undone.
          </p>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletingRoom(null)}
              className="border-[#2A2A45] text-white/60 hover:text-white hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ─── Placeholder Sections ─── */

function SectionPlaceholder({ 
  icon: Icon, 
  title, 
  description 
}: { 
  icon: React.ElementType
  title: string
  description: string 
}) {
  return (
    <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8 text-center">
      <div className="w-16 h-16 rounded-2xl bg-[#F37020]/10 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-[#F37020]" />
      </div>
      <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
        {title}
      </h3>
      <p className="text-sm text-white/40 max-w-md mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
        {description}
      </p>
    </div>
  )
}

/* ─── Bot System Section ─── */

function BotSystemSection() {
  const { token } = useAuth()
  const [rooms, setRooms] = React.useState<PokerRoom[]>([])
  const [selectedRoomId, setSelectedRoomId] = React.useState<string>("")
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [addingBot, setAddingBot] = React.useState(false)
  const [removingBot, setRemovingBot] = React.useState<number | null>(null)
  const [error, setError] = React.useState<string | null>(null)

  // Selected room data
  const selectedRoom = rooms.find(r => r._id === selectedRoomId)
  const botConfig = selectedRoom?.botConfig || {
    enabled: false,
    difficulty: "easy" as const,
    maxBots: 3,
    autoFill: false,
    autoFillTimeout: 5,
  }
  const currentBots = (selectedRoom?.players || []).filter(
    (p) => p.isBot === true
  )
  const humanCount = (selectedRoom?.players?.length || 0) - currentBots.length
  const maxBotsAllowed = Math.min(
    botConfig.maxBots,
    (selectedRoom?.maxPlayers || 6) - 1 - humanCount
  )

  // Fetch rooms
  const fetchRooms = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/admin/poker/rooms", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setRooms(data.rooms || [])
        // Auto-select first room if none selected
        if (!selectedRoomId && data.rooms?.length > 0) {
          setSelectedRoomId(data.rooms[0]._id)
        }
      } else {
        setError(data.message || "Failed to fetch rooms")
      }
    } catch (err) {
      setError("Network error fetching rooms")
    } finally {
      setLoading(false)
    }
  }, [token, selectedRoomId])

  React.useEffect(() => {
    fetchRooms()
  }, [fetchRooms])

  // Toggle bots enabled
  const toggleBots = async (enabled: boolean) => {
    if (!token || !selectedRoomId) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${selectedRoomId}/bots/toggle`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ enabled }),
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        setError(data.message || "Failed to toggle bots")
      }
    } catch (err) {
      setError("Network error toggling bots")
    } finally {
      setSaving(false)
    }
  }

  // Update bot settings
  const updateBotSettings = async (updates: Partial<typeof botConfig>) => {
    if (!token || !selectedRoomId) return
    setSaving(true)
    setError(null)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${selectedRoomId}/bots/toggle`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled: botConfig.enabled,
          ...updates,
        }),
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        setError(data.message || "Failed to update settings")
      }
    } catch (err) {
      setError("Network error updating settings")
    } finally {
      setSaving(false)
    }
  }

  // Add bot
  const addBot = async () => {
    if (!token || !selectedRoomId) return
    setAddingBot(true)
    setError(null)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${selectedRoomId}/bots/add`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ difficulty: botConfig.difficulty }),
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        setError(data.message || "Failed to add bot")
      }
    } catch (err) {
      setError("Network error adding bot")
    } finally {
      setAddingBot(false)
    }
  }

  // Remove bot
  const removeBot = async (seatIndex: number) => {
    if (!token || !selectedRoomId) return
    setRemovingBot(seatIndex)
    setError(null)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${selectedRoomId}/bots/remove/${seatIndex}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        setError(data.message || "Failed to remove bot")
      }
    } catch (err) {
      setError("Network error removing bot")
    } finally {
      setRemovingBot(null)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8">
        <div className="flex items-center justify-center gap-3 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span style={{ fontFamily: "Outfit, sans-serif" }}>Loading rooms...</span>
        </div>
      </div>
    )
  }

  // No rooms state
  if (rooms.length === 0) {
    return (
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#F37020]/10 flex items-center justify-center mx-auto mb-4">
          <Bot className="w-8 h-8 text-[#F37020]" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          No Rooms Available
        </h3>
        <p className="text-sm text-white/40 max-w-md mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
          Create a poker room first before configuring bots.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Room Selector */}
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Label className="text-white/70 whitespace-nowrap" style={{ fontFamily: "Outfit, sans-serif" }}>
            Select Room:
          </Label>
          <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
            <SelectTrigger className="w-full sm:w-[280px]">
              <SelectValue placeholder="Select a room" />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((room) => (
                <SelectItem key={room._id} value={room._id}>
                  {room.name} ({room.players?.length || 0}/{room.maxPlayers})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchRooms()}
            className="border-[#2A2A45] text-white/60 hover:text-white hover:bg-white/5 sm:ml-auto"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}

      {selectedRoom && (
        <>
          {/* Bot Configuration */}
          <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4 space-y-6">
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Bot Configuration
            </h3>

            {/* Enable Bots Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Enable Bots</Label>
                <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Allow AI bots to join this room
                </p>
              </div>
              <button
                onClick={() => toggleBots(!botConfig.enabled)}
                disabled={saving}
                className="relative"
              >
                {botConfig.enabled ? (
                  <ToggleRight className="w-10 h-10 text-[#F37020]" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-white/30" />
                )}
              </button>
            </div>

            {botConfig.enabled && (
              <>
                {/* Difficulty */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Bot Difficulty
                    </Label>
                    <Select
                      value={botConfig.difficulty}
                      onValueChange={(val) => updateBotSettings({ difficulty: val as "easy" | "medium" | "hard" })}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy - Conservative, predictable</SelectItem>
                        <SelectItem value="medium">Medium - Balanced play</SelectItem>
                        <SelectItem value="hard">Hard - Aggressive, unpredictable</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Max Bots */}
                  <div className="space-y-2">
                    <Label className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                      Max Bots: {botConfig.maxBots}
                    </Label>
                    <input
                      type="range"
                      min={1}
                      max={5}
                      value={botConfig.maxBots}
                      onChange={(e) => updateBotSettings({ maxBots: Number(e.target.value) })}
                      className="w-full h-2 bg-[#2A2A45] rounded-lg appearance-none cursor-pointer accent-[#F37020]"
                    />
                    <div className="flex justify-between text-xs text-white/30" style={{ fontFamily: "Outfit, sans-serif" }}>
                      <span>1</span>
                      <span>5</span>
                    </div>
                  </div>
                </div>

                {/* Auto-fill settings */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 pt-2 border-t border-[#2A2A45]">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => updateBotSettings({ autoFill: !botConfig.autoFill })}
                      disabled={saving}
                      className="relative"
                    >
                      {botConfig.autoFill ? (
                        <ToggleRight className="w-8 h-8 text-[#F37020]" />
                      ) : (
                        <ToggleLeft className="w-8 h-8 text-white/30" />
                      )}
                    </button>
                    <div>
                      <Label className="text-white" style={{ fontFamily: "Outfit, sans-serif" }}>Auto-fill</Label>
                      <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Add bots when room waits too long
                      </p>
                    </div>
                  </div>

                  {botConfig.autoFill && (
                    <div className="flex items-center gap-2 sm:ml-auto">
                      <Label className="text-white/70 whitespace-nowrap" style={{ fontFamily: "Outfit, sans-serif" }}>
                        Timeout:
                      </Label>
                      <Input
                        type="number"
                        min={1}
                        max={30}
                        value={botConfig.autoFillTimeout}
                        onChange={(e) => updateBotSettings({ autoFillTimeout: Number(e.target.value) })}
                        className="w-20 bg-[#1A1A2E] border-[#2A2A45] text-white text-center"
                      />
                      <span className="text-white/40 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>min</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Current Bots */}
          {botConfig.enabled && (
            <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Current Bots ({currentBots.length})
                </h3>
                <Button
                  size="sm"
                  onClick={addBot}
                  disabled={addingBot || currentBots.length >= maxBotsAllowed}
                  className="bg-[#F37020] hover:bg-[#F37020]/90 text-white disabled:opacity-50"
                >
                  {addingBot ? (
                    <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                  ) : (
                    <Plus className="w-4 h-4 mr-1.5" />
                  )}
                  Add Bot
                </Button>
              </div>

              {currentBots.length === 0 ? (
                <div className="text-center py-6 text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                  No bots in this room yet. Click "Add Bot" to add one.
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Desktop Table */}
                  <div className="hidden md:block">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-[#2A2A45]">
                          <th className="text-left px-3 py-2 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Name</th>
                          <th className="text-left px-3 py-2 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Seat</th>
                          <th className="text-left px-3 py-2 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Difficulty</th>
                          <th className="text-right px-3 py-2 text-sm font-medium text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentBots.map((bot) => (
                          <tr key={bot.seatIndex} className="border-b border-[#2A2A45] last:border-0 hover:bg-white/[0.02]">
                            <td className="px-3 py-2 text-sm text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                              <span className="inline-flex items-center gap-2">
                                <Bot className="w-4 h-4 text-[#F37020]" />
                                {bot.displayName || "Bot"}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-sm text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                              #{bot.seatIndex + 1}
                            </td>
                            <td className="px-3 py-2 text-sm">
                              <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                bot.difficulty === "hard"
                                  ? "bg-red-500/15 text-red-400"
                                  : bot.difficulty === "medium"
                                  ? "bg-yellow-500/15 text-yellow-400"
                                  : "bg-green-500/15 text-green-400"
                              }`} style={{ fontFamily: "Outfit, sans-serif" }}>
                                {bot.difficulty || botConfig.difficulty}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right">
                              <button
                                onClick={() => removeBot(bot.seatIndex)}
                                disabled={removingBot === bot.seatIndex}
                                className="p-1.5 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                              >
                                {removingBot === bot.seatIndex ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <MinusCircle className="w-4 h-4" />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile Cards */}
                  <div className="md:hidden space-y-2">
                    {currentBots.map((bot) => (
                      <div
                        key={bot.seatIndex}
                        className="flex items-center justify-between bg-[#1A1A2E] rounded-lg p-3"
                      >
                        <div className="flex items-center gap-3">
                          <Bot className="w-5 h-5 text-[#F37020]" />
                          <div>
                            <div className="text-sm text-white font-medium" style={{ fontFamily: "Outfit, sans-serif" }}>
                              {bot.displayName || "Bot"}
                            </div>
                            <div className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                              Seat #{bot.seatIndex + 1} • {bot.difficulty || botConfig.difficulty}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={() => removeBot(bot.seatIndex)}
                          disabled={removingBot === bot.seatIndex}
                          className="p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-red-500/10 transition-colors disabled:opacity-50"
                        >
                          {removingBot === bot.seatIndex ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <MinusCircle className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {currentBots.length >= maxBotsAllowed && (
                <p className="text-xs text-white/40 text-center" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Maximum bots reached. Remove a bot or increase max bots limit.
                </p>
              )}
            </div>
          )}
        </>
      )}
    </div>
  )
}

/* ─── Game Rules Section ─── */

function GameRulesSection() {
  const { token } = useAuth()
  const [rooms, setRooms] = React.useState<PokerRoom[]>([])
  const [selectedRoomId, setSelectedRoomId] = React.useState<string>("")
  const [loading, setLoading] = React.useState(true)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)

  // Form state
  const [startingChips, setStartingChips] = React.useState(1000)
  const [smallBlind, setSmallBlind] = React.useState(1)
  const [bigBlind, setBigBlind] = React.useState(2)
  const [blindIncreaseEnabled, setBlindIncreaseEnabled] = React.useState(false)
  const [blindIncreaseInterval, setBlindIncreaseInterval] = React.useState(15)
  const [handTimeLimit, setHandTimeLimit] = React.useState(30)
  const [sidePotEnabled, setSidePotEnabled] = React.useState(true)

  // Get selected room
  const selectedRoom = rooms.find(r => r._id === selectedRoomId)

  // Fetch rooms
  const fetchRooms = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetch("/api/admin/poker/rooms", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setRooms(data.rooms || [])
        if (!selectedRoomId && data.rooms?.length > 0) {
          setSelectedRoomId(data.rooms[0]._id)
        }
      } else {
        setError(data.message || "Failed to fetch rooms")
      }
    } catch (err) {
      setError("Network error fetching rooms")
    } finally {
      setLoading(false)
    }
  }, [token, selectedRoomId])

  React.useEffect(() => {
    fetchRooms()
  }, [fetchRooms])

  // Populate form when room changes
  React.useEffect(() => {
    if (selectedRoom) {
      setStartingChips(selectedRoom.startingChips || 1000)
      setSmallBlind(selectedRoom.smallBlind || 1)
      setBigBlind(selectedRoom.bigBlind || 2)
      setBlindIncreaseInterval(selectedRoom.blindIncreaseInterval || 15)
      setBlindIncreaseEnabled(selectedRoom.blindIncreaseInterval > 0)
      setHandTimeLimit(selectedRoom.handTimeLimit || 30)
      // sidePotEnabled not stored in model yet, default true
      setSidePotEnabled(true)
    }
  }, [selectedRoom])

  // Save rules
  const handleSave = async () => {
    if (!token || !selectedRoomId) return
    setSaving(true)
    setError(null)
    setSuccess(null)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${selectedRoomId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          startingChips,
          smallBlind,
          bigBlind,
          blindIncreaseInterval: blindIncreaseEnabled ? blindIncreaseInterval : 0,
          handTimeLimit,
        }),
      })
      const data = await res.json()
      if (data.success) {
        setSuccess("Game rules saved successfully!")
        fetchRooms()
        setTimeout(() => setSuccess(null), 3000)
      } else {
        setError(data.message || "Failed to save rules")
      }
    } catch (err) {
      setError("Network error saving rules")
    } finally {
      setSaving(false)
    }
  }

  // Loading state
  if (loading) {
    return (
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8">
        <div className="flex items-center justify-center gap-3 text-white/40">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span style={{ fontFamily: "Outfit, sans-serif" }}>Loading rooms...</span>
        </div>
      </div>
    )
  }

  // No rooms state
  if (rooms.length === 0) {
    return (
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-8 text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#F37020]/10 flex items-center justify-center mx-auto mb-4">
          <Coins className="w-8 h-8 text-[#F37020]" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          No Rooms Available
        </h3>
        <p className="text-sm text-white/40 max-w-md mx-auto" style={{ fontFamily: "Outfit, sans-serif" }}>
          Create a poker room first before configuring game rules.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Room Selector */}
      <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <Label className="text-white/70 whitespace-nowrap" style={{ fontFamily: "Outfit, sans-serif" }}>
            Select Room:
          </Label>
          <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
            <SelectTrigger className="w-full sm:w-[280px]">
              <SelectValue placeholder="Select a room" />
            </SelectTrigger>
            <SelectContent>
              {rooms.map((room) => (
                <SelectItem key={room._id} value={room._id}>
                  {room.name} ({room.status})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchRooms()}
            className="border-[#2A2A45] text-white/60 hover:text-white hover:bg-white/5 sm:ml-auto"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-3 text-green-400 text-sm">
          {success}
        </div>
      )}

      {selectedRoom && (
        <div className="bg-[#111117] border border-[#2A2A45] rounded-xl p-4 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
              Game Rules for "{selectedRoom.name}"
            </h3>
            <span className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
              Changes apply to new hands only
            </span>
          </div>

          {/* Form Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Starting Chips */}
            <div className="space-y-2">
              <Label htmlFor="startingChips" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                Starting Chips
              </Label>
              <Input
                id="startingChips"
                type="number"
                min={100}
                max={100000}
                value={startingChips}
                onChange={(e) => setStartingChips(Number(e.target.value))}
                className="bg-[#1A1A2E] border-[#2A2A45] text-white"
              />
              <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                Chips each player starts with
              </p>
            </div>

            {/* Hand Time Limit */}
            <div className="space-y-2">
              <Label htmlFor="handTimeLimit" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                Hand Time Limit (seconds)
              </Label>
              <Input
                id="handTimeLimit"
                type="number"
                min={10}
                max={120}
                value={handTimeLimit}
                onChange={(e) => setHandTimeLimit(Number(e.target.value))}
                className="bg-[#1A1A2E] border-[#2A2A45] text-white"
              />
              <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                Time each player has to act (10-120s)
              </p>
            </div>
          </div>

          {/* Blind Structure */}
          <div className="space-y-4 pt-4 border-t border-[#2A2A45]">
            <h4 className="text-sm font-semibold text-white/80" style={{ fontFamily: "Outfit, sans-serif" }}>
              Blind Structure
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              {/* Small Blind */}
              <div className="space-y-2">
                <Label htmlFor="smallBlind" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Small Blind
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input
                    id="smallBlind"
                    type="number"
                    min={1}
                    value={smallBlind}
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      setSmallBlind(val)
                      if (bigBlind < val * 2) setBigBlind(val * 2)
                    }}
                    className="bg-[#1A1A2E] border-[#2A2A45] text-white pl-7"
                  />
                </div>
              </div>

              {/* Big Blind */}
              <div className="space-y-2">
                <Label htmlFor="bigBlind" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Big Blind
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40">$</span>
                  <Input
                    id="bigBlind"
                    type="number"
                    min={smallBlind}
                    value={bigBlind}
                    onChange={(e) => setBigBlind(Number(e.target.value))}
                    className="bg-[#1A1A2E] border-[#2A2A45] text-white pl-7"
                  />
                </div>
                <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Usually 2x small blind
                </p>
              </div>

              {/* Current Stakes Display */}
              <div className="space-y-2 flex flex-col justify-center">
                <div className="text-center py-3 px-4 bg-[#1A1A2E] rounded-lg border border-[#2A2A45]">
                  <span className="text-xs text-white/40 block mb-1" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Stakes
                  </span>
                  <span className="text-lg font-semibold text-[#FFD700]" style={{ fontFamily: "Outfit, sans-serif" }}>
                    ${smallBlind}/${bigBlind}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Blind Increase */}
          <div className="space-y-4 pt-4 border-t border-[#2A2A45]">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Automatic Blind Increase
                </Label>
                <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Blinds increase periodically during tournament play
                </p>
              </div>
              <button
                onClick={() => setBlindIncreaseEnabled(!blindIncreaseEnabled)}
                className="relative"
              >
                {blindIncreaseEnabled ? (
                  <ToggleRight className="w-10 h-10 text-[#F37020]" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-white/30" />
                )}
              </button>
            </div>

            {blindIncreaseEnabled && (
              <div className="pl-4 border-l-2 border-[#F37020]/30">
                <div className="space-y-2">
                  <Label htmlFor="blindInterval" className="text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Increase Interval (minutes)
                  </Label>
                  <Input
                    id="blindInterval"
                    type="number"
                    min={5}
                    max={60}
                    value={blindIncreaseInterval}
                    onChange={(e) => setBlindIncreaseInterval(Number(e.target.value))}
                    className="bg-[#1A1A2E] border-[#2A2A45] text-white w-32"
                  />
                  <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                    Blinds double every {blindIncreaseInterval} minutes
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Side Pot Rules */}
          <div className="space-y-4 pt-4 border-t border-[#2A2A45]">
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-white" style={{ fontFamily: "Outfit, sans-serif" }}>
                  All-in & Side Pot Rules
                </Label>
                <p className="text-xs text-white/40" style={{ fontFamily: "Outfit, sans-serif" }}>
                  Enable complex side pot calculations for all-in situations
                </p>
              </div>
              <button
                onClick={() => setSidePotEnabled(!sidePotEnabled)}
                className="relative"
              >
                {sidePotEnabled ? (
                  <ToggleRight className="w-10 h-10 text-[#F37020]" />
                ) : (
                  <ToggleLeft className="w-10 h-10 text-white/30" />
                )}
              </button>
            </div>

            {sidePotEnabled && (
              <div className="pl-4 border-l-2 border-[#F37020]/30 text-sm text-white/50" style={{ fontFamily: "Outfit, sans-serif" }}>
                When enabled, if a player goes all-in for less than the full bet, a side pot is created. 
                Players can only win from pots they contributed to.
              </div>
            )}
          </div>

          {/* Save Button */}
          <div className="pt-4 border-t border-[#2A2A45] flex justify-end">
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#F37020] hover:bg-[#F37020]/90 text-white px-6"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Rules"
              )}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

/* ─── Monitoring Section ─── */

interface HandHistoryItem {
  _id: string
  handNumber: number
  players: Array<{
    displayName: string
    startingChips: number
    finalChips: number
    isBot: boolean
  }>
  potSize: number
  winnerDisplayName: string
  communityCards: string[]
  actions: Array<{
    playerDisplayName: string
    action: string
    amount?: number
    timestamp: string
  }>
  startedAt: string
  endedAt: string
}

function MonitoringSection() {
  const { token } = useAuth()
  const [rooms, setRooms] = React.useState<PokerRoom[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [selectedRoomId, setSelectedRoomId] = React.useState<string | null>(null)
  const [handHistory, setHandHistory] = React.useState<HandHistoryItem[]>([])
  const [loadingHistory, setLoadingHistory] = React.useState(false)
  const [expandedHand, setExpandedHand] = React.useState<string | null>(null)
  const [actionInProgress, setActionInProgress] = React.useState<string | null>(null)
  const [confirmAction, setConfirmAction] = React.useState<{
    type: "end" | "kick"
    roomId: string
    playerId?: string
    playerName?: string
  } | null>(null)
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date())

  // Fetch rooms
  const fetchRooms = React.useCallback(async () => {
    if (!token) return
    try {
      const res = await fetch("/api/admin/poker/rooms", {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setRooms(data.rooms || [])
        setError(null)
      } else {
        setError(data.message || "Failed to fetch rooms")
      }
    } catch (err) {
      setError("Failed to connect to server")
    } finally {
      setLoading(false)
      setLastUpdated(new Date())
    }
  }, [token])

  // Initial load + polling
  React.useEffect(() => {
    fetchRooms()
    const interval = setInterval(fetchRooms, 5000)
    return () => clearInterval(interval)
  }, [fetchRooms])

  // Fetch hand history for selected room
  const fetchHandHistory = React.useCallback(async (roomId: string) => {
    if (!token) return
    setLoadingHistory(true)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${roomId}/hands?limit=20`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setHandHistory(data.hands || [])
      }
    } catch (err) {
      console.error("Failed to fetch hand history:", err)
    } finally {
      setLoadingHistory(false)
    }
  }, [token])

  const handleSelectRoom = (roomId: string) => {
    if (selectedRoomId === roomId) {
      setSelectedRoomId(null)
      setHandHistory([])
    } else {
      setSelectedRoomId(roomId)
      fetchHandHistory(roomId)
    }
  }

  const handlePause = async (roomId: string) => {
    if (!token) return
    setActionInProgress(`pause-${roomId}`)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${roomId}/pause`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        alert(data.message || "Failed to pause game")
      }
    } catch {
      alert("Failed to pause game")
    } finally {
      setActionInProgress(null)
    }
  }

  const handleResume = async (roomId: string) => {
    if (!token) return
    setActionInProgress(`resume-${roomId}`)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${roomId}/resume`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
      } else {
        alert(data.message || "Failed to resume game")
      }
    } catch {
      alert("Failed to resume game")
    } finally {
      setActionInProgress(null)
    }
  }

  const handleEnd = async (roomId: string) => {
    if (!token) return
    setActionInProgress(`end-${roomId}`)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${roomId}/end`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
        setConfirmAction(null)
      } else {
        alert(data.message || "Failed to end game")
      }
    } catch {
      alert("Failed to end game")
    } finally {
      setActionInProgress(null)
    }
  }

  const handleKick = async (roomId: string, seatIndex: number) => {
    if (!token) return
    setActionInProgress(`kick-${roomId}-${seatIndex}`)
    try {
      const res = await fetch(`/api/admin/poker/rooms/${roomId}/kick/${seatIndex}`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        fetchRooms()
        setConfirmAction(null)
      } else {
        alert(data.message || "Failed to kick player")
      }
    } catch {
      alert("Failed to kick player")
    } finally {
      setActionInProgress(null)
    }
  }

  const activeTables = rooms.filter(r => r.status === "active" || r.status === "playing" || r.status === "paused").length
  const totalPlayers = rooms.reduce((sum, r) => sum + (r.players?.length || 0), 0)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-[#F37020] animate-spin" />
      </div>
    )
  }

  return (
    <div className="space-y-6" style={{ fontFamily: "Outfit, sans-serif" }}>
      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-4">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Active Tables</div>
          <div className="text-2xl font-bold text-[#FFD700]">{activeTables}</div>
        </div>
        <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-4">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Total Players</div>
          <div className="text-2xl font-bold text-[#32CD32]">{totalPlayers}</div>
        </div>
        <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-4">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Waiting Rooms</div>
          <div className="text-2xl font-bold text-yellow-400">{rooms.filter(r => r.status === "waiting").length}</div>
        </div>
        <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-4">
          <div className="text-white/50 text-xs uppercase tracking-wider mb-1">Last Update</div>
          <div className="text-sm text-white/70">{lastUpdated.toLocaleTimeString()}</div>
          <button
            onClick={() => fetchRooms()}
            className="text-[#F37020] text-xs hover:underline mt-1 flex items-center gap-1"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {/* Rooms List */}
      <div className="space-y-4">
        <h3 className="text-white/70 text-sm font-medium uppercase tracking-wider">Live Rooms</h3>

        {rooms.length === 0 ? (
          <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-8 text-center">
            <Activity className="w-12 h-12 text-white/20 mx-auto mb-3" />
            <p className="text-white/50">No poker rooms created yet</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {rooms.map((room) => {
              const isSelected = selectedRoomId === room._id
              const isActiveOrPaused = room.status === "active" || room.status === "playing" || room.status === "paused"

              return (
                <div
                  key={room._id}
                  className={`bg-[#1a1a2e] border rounded-xl overflow-hidden transition-all ${
                    isSelected ? "border-[#F37020]/50" : "border-white/10"
                  }`}
                >
                  {/* Room Header */}
                  <div
                    onClick={() => handleSelectRoom(room._id)}
                    className="p-4 cursor-pointer hover:bg-white/[0.02]"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <Spade className="w-5 h-5 text-[#F37020]" />
                        <span className="text-white font-medium">{room.name}</span>
                        <StatusBadge status={room.status} />
                      </div>
                      <div className="flex items-center gap-2">
                        {(room.status === "active" || room.status === "playing") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handlePause(room._id); }}
                            disabled={actionInProgress === `pause-${room._id}`}
                            className="border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 h-8 px-3 text-xs"
                          >
                            {actionInProgress === `pause-${room._id}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Pause"
                            )}
                          </Button>
                        )}
                        {room.status === "paused" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleResume(room._id); }}
                            disabled={actionInProgress === `resume-${room._id}`}
                            className="border-green-500/30 text-green-400 hover:bg-green-500/10 h-8 px-3 text-xs"
                          >
                            {actionInProgress === `resume-${room._id}` ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              "Resume"
                            )}
                          </Button>
                        )}
                        {isActiveOrPaused && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); setConfirmAction({ type: "end", roomId: room._id }); }}
                            className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-8 px-3 text-xs"
                          >
                            End Game
                          </Button>
                        )}
                        <ChevronDown
                          className={`w-5 h-5 text-white/30 transition-transform ${isSelected ? "rotate-180" : ""}`}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-4 text-sm">
                      <div className="flex items-center gap-1 text-white/50">
                        <Users className="w-4 h-4" />
                        <span>{room.players?.length || 0}/{room.maxPlayers}</span>
                      </div>
                      <div className="flex items-center gap-1 text-white/50">
                        <Coins className="w-4 h-4" />
                        <span>${room.smallBlind}/${room.bigBlind}</span>
                      </div>
                      <div className="text-white/50">
                        Buy-in: <span className="text-[#FFD700]">${room.buyIn}</span>
                      </div>
                    </div>
                  </div>

                  {/* Expanded Content */}
                  <AnimatePresence>
                    {isSelected && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-white/10"
                      >
                        <div className="p-4 space-y-4">
                          {/* Players Table */}
                          <div>
                            <h4 className="text-white/70 text-xs uppercase tracking-wider mb-2">Current Players</h4>
                            {room.players && room.players.length > 0 ? (
                              <div className="bg-black/20 rounded-lg overflow-hidden">
                                <table className="w-full hidden md:table">
                                  <thead>
                                    <tr className="text-left text-white/50 text-xs uppercase">
                                      <th className="p-3">Seat</th>
                                      <th className="p-3">Player</th>
                                      <th className="p-3">Type</th>
                                      <th className="p-3 text-right">Chips</th>
                                      <th className="p-3 text-right">Actions</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {room.players.map((player, idx) => (
                                      <tr key={idx} className="border-t border-white/5">
                                        <td className="p-3 text-white/70">{player.seatIndex + 1}</td>
                                        <td className="p-3 text-white">{player.displayName || `Player ${idx + 1}`}</td>
                                        <td className="p-3">
                                          {player.isBot ? (
                                            <span className="text-purple-400 flex items-center gap-1">
                                              <Bot className="w-3 h-3" /> Bot ({player.difficulty})
                                            </span>
                                          ) : (
                                            <span className="text-green-400">Human</span>
                                          )}
                                        </td>
                                        <td className="p-3 text-right text-[#FFD700] font-mono">
                                          {(player as any).chips?.toLocaleString() || room.startingChips?.toLocaleString() || "—"}
                                        </td>
                                        <td className="p-3 text-right">
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setConfirmAction({
                                              type: "kick",
                                              roomId: room._id,
                                              playerId: String(player.seatIndex),
                                              playerName: player.displayName || `Player ${idx + 1}`
                                            })}
                                            disabled={actionInProgress?.startsWith(`kick-${room._id}`)}
                                            className="text-red-400 hover:text-red-300 hover:bg-red-500/10 h-7 px-2"
                                          >
                                            <MinusCircle className="w-4 h-4" />
                                          </Button>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>

                                {/* Mobile Cards */}
                                <div className="md:hidden space-y-2 p-2">
                                  {room.players.map((player, idx) => (
                                    <div
                                      key={idx}
                                      className="bg-white/[0.02] rounded-lg p-3 flex items-center justify-between"
                                    >
                                      <div>
                                        <div className="text-white text-sm">{player.displayName || `Player ${idx + 1}`}</div>
                                        <div className="flex items-center gap-2 text-xs mt-1">
                                          <span className="text-white/50">Seat {player.seatIndex + 1}</span>
                                          {player.isBot ? (
                                            <span className="text-purple-400 flex items-center gap-1">
                                              <Bot className="w-3 h-3" /> {player.difficulty}
                                            </span>
                                          ) : (
                                            <span className="text-green-400">Human</span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-2">
                                        <span className="text-[#FFD700] font-mono text-sm">
                                          {(player as any).chips?.toLocaleString() || room.startingChips?.toLocaleString() || "—"}
                                        </span>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          onClick={() => setConfirmAction({
                                            type: "kick",
                                            roomId: room._id,
                                            playerId: String(player.seatIndex),
                                            playerName: player.displayName || `Player ${idx + 1}`
                                          })}
                                          className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                                        >
                                          <MinusCircle className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <div className="bg-black/20 rounded-lg p-4 text-center text-white/30 text-sm">
                                No players in this room
                              </div>
                            )}
                          </div>

                          {/* Hand History */}
                          <div>
                            <h4 className="text-white/70 text-xs uppercase tracking-wider mb-2">Recent Hands</h4>
                            {loadingHistory ? (
                              <div className="flex items-center justify-center py-4">
                                <Loader2 className="w-5 h-5 text-[#F37020] animate-spin" />
                              </div>
                            ) : handHistory.length > 0 ? (
                              <div className="space-y-2">
                                {handHistory.map((hand) => (
                                  <div
                                    key={hand._id}
                                    className="bg-black/20 rounded-lg overflow-hidden"
                                  >
                                    <div
                                      onClick={() => setExpandedHand(expandedHand === hand._id ? null : hand._id)}
                                      className="p-3 cursor-pointer hover:bg-white/[0.02] flex items-center justify-between"
                                    >
                                      <div className="flex items-center gap-3">
                                        <span className="text-white/50 text-sm">#{hand.handNumber}</span>
                                        <span className="text-[#FFD700] font-mono text-sm">Pot: ${hand.potSize}</span>
                                        <span className="text-green-400 text-sm">Winner: {hand.winnerDisplayName}</span>
                                      </div>
                                      <ChevronDown
                                        className={`w-4 h-4 text-white/30 transition-transform ${
                                          expandedHand === hand._id ? "rotate-180" : ""
                                        }`}
                                      />
                                    </div>
                                    <AnimatePresence>
                                      {expandedHand === hand._id && (
                                        <motion.div
                                          initial={{ height: 0 }}
                                          animate={{ height: "auto" }}
                                          exit={{ height: 0 }}
                                          className="overflow-hidden border-t border-white/5"
                                        >
                                          <div className="p-3 space-y-2 text-sm">
                                            <div className="text-white/50">
                                              Community: {hand.communityCards?.join(" ") || "None"}
                                            </div>
                                            <div className="text-white/50">
                                              Players: {hand.players?.map(p => p.displayName).join(", ")}
                                            </div>
                                            {hand.actions && hand.actions.length > 0 && (
                                              <div className="text-xs text-white/30 max-h-32 overflow-y-auto">
                                                {hand.actions.map((action, i) => (
                                                  <div key={i}>
                                                    {action.playerDisplayName}: {action.action}
                                                    {action.amount ? ` $${action.amount}` : ""}
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        </motion.div>
                                      )}
                                    </AnimatePresence>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="bg-black/20 rounded-lg p-4 text-center text-white/30 text-sm">
                                No hands played yet
                              </div>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog open={confirmAction !== null} onOpenChange={() => setConfirmAction(null)}>
        <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
              {confirmAction?.type === "end" ? "End Game" : "Kick Player"}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 text-white/70" style={{ fontFamily: "Outfit, sans-serif" }}>
            {confirmAction?.type === "end" ? (
              <p>Are you sure you want to end this game? All players will be removed and the room will be closed.</p>
            ) : (
              <p>Are you sure you want to kick <strong className="text-white">{confirmAction?.playerName}</strong> from the room? Their chips will be refunded.</p>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              className="border-white/20 text-white/70 hover:bg-white/5"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (confirmAction?.type === "end") {
                  handleEnd(confirmAction.roomId)
                } else if (confirmAction?.type === "kick" && confirmAction.playerId !== undefined) {
                  handleKick(confirmAction.roomId, parseInt(confirmAction.playerId, 10))
                }
              }}
              disabled={actionInProgress !== null}
              className="bg-red-600 hover:bg-red-700"
            >
              {actionInProgress !== null ? (
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              {confirmAction?.type === "end" ? "End Game" : "Kick Player"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

/* ─── Analytics Section ─── */

interface AnalyticsData {
  totalHands: number
  averagePotSize: number
  activePlayers: number
  botWinRate: number
  activeRooms: number
  totalChipsInPlay: number
}

type DateRange = "24h" | "7d" | "30d" | "all"

function AnalyticsSection() {
  const { token } = useAuth()
  const [analytics, setAnalytics] = React.useState<AnalyticsData | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [dateRange, setDateRange] = React.useState<DateRange>("all")
  const [lastUpdated, setLastUpdated] = React.useState<Date>(new Date())

  const fetchAnalytics = React.useCallback(async () => {
    if (!token) return
    setLoading(true)
    try {
      const res = await fetch(`/api/admin/poker/analytics?range=${dateRange}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      const data = await res.json()
      if (data.success) {
        setAnalytics(data.analytics)
        setError(null)
      } else {
        setError(data.message || "Failed to fetch analytics")
      }
    } catch {
      setError("Failed to connect to server")
    } finally {
      setLoading(false)
      setLastUpdated(new Date())
    }
  }, [token, dateRange])

  React.useEffect(() => {
    fetchAnalytics()
  }, [fetchAnalytics])

  const statCards = [
    {
      label: "Total Hands",
      value: analytics?.totalHands ?? 0,
      format: (v: number) => v.toLocaleString(),
      icon: Activity,
      color: "text-[#FFD700]",
      bgColor: "bg-[#FFD700]/10",
    },
    {
      label: "Average Pot",
      value: analytics?.averagePotSize ?? 0,
      format: (v: number) => `$${v.toLocaleString()}`,
      icon: Coins,
      color: "text-[#32CD32]",
      bgColor: "bg-[#32CD32]/10",
    },
    {
      label: "Active Players",
      value: analytics?.activePlayers ?? 0,
      format: (v: number) => v.toLocaleString(),
      icon: Users,
      color: "text-[#F37020]",
      bgColor: "bg-[#F37020]/10",
    },
    {
      label: "Bot Win Rate",
      value: analytics?.botWinRate ?? 0,
      format: (v: number) => `${v.toFixed(1)}%`,
      icon: Bot,
      color: "text-purple-400",
      bgColor: "bg-purple-400/10",
    },
    {
      label: "Active Rooms",
      value: analytics?.activeRooms ?? 0,
      format: (v: number) => v.toLocaleString(),
      icon: Spade,
      color: "text-white",
      bgColor: "bg-white/10",
    },
    {
      label: "Chips in Play",
      value: analytics?.totalChipsInPlay ?? 0,
      format: (v: number) => v.toLocaleString(),
      icon: Coins,
      color: "text-[#FFD700]",
      bgColor: "bg-[#FFD700]/10",
    },
  ]

  const dateRanges: { value: DateRange; label: string }[] = [
    { value: "24h", label: "Last 24h" },
    { value: "7d", label: "Last 7 Days" },
    { value: "30d", label: "Last 30 Days" },
    { value: "all", label: "All Time" },
  ]

  return (
    <div className="space-y-6" style={{ fontFamily: "Outfit, sans-serif" }}>
      {/* Header with Date Range and Refresh */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2">
          {dateRanges.map((range) => (
            <button
              key={range.value}
              onClick={() => setDateRange(range.value)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                dateRange === range.value
                  ? "bg-[#F37020]/15 text-[#F37020] border border-[#F37020]/30"
                  : "bg-white/[0.04] text-white/50 hover:text-white/70 hover:bg-white/[0.06] border border-transparent"
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 text-sm text-white/50">
          <span>Updated: {lastUpdated.toLocaleTimeString()}</span>
          <button
            onClick={() => fetchAnalytics()}
            disabled={loading}
            className="flex items-center gap-1 text-[#F37020] hover:text-[#F37020]/80 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg p-4 text-sm">
          {error}
        </div>
      )}

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {statCards.map((stat) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#1a1a2e] border border-white/10 rounded-xl p-4 relative overflow-hidden"
          >
            <div className={`absolute top-3 right-3 p-2 rounded-lg ${stat.bgColor}`}>
              <stat.icon className={`w-4 h-4 ${stat.color}`} />
            </div>
            <div className="text-white/50 text-xs uppercase tracking-wider mb-2">
              {stat.label}
            </div>
            {loading ? (
              <div className="h-8 bg-white/5 rounded animate-pulse" />
            ) : (
              <motion.div
                key={`${stat.label}-${stat.value}`}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className={`text-2xl font-bold ${stat.color}`}
              >
                {stat.format(stat.value)}
              </motion.div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Additional Info */}
      <div className="bg-[#1a1a2e] border border-white/10 rounded-xl p-6">
        <h3 className="text-white/70 text-sm font-medium uppercase tracking-wider mb-4">
          Quick Insights
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-[#F37020]/10">
              <BarChart3 className="w-4 h-4 text-[#F37020]" />
            </div>
            <div>
              <div className="text-white font-medium">Performance Summary</div>
              <div className="text-white/50 mt-1">
                {analytics?.totalHands === 0 ? (
                  "No hands played yet. Analytics will populate as games are played."
                ) : (
                  `${analytics?.totalHands.toLocaleString()} hands played with an average pot of $${analytics?.averagePotSize.toLocaleString()}.`
                )}
              </div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-purple-400/10">
              <Bot className="w-4 h-4 text-purple-400" />
            </div>
            <div>
              <div className="text-white font-medium">Bot Performance</div>
              <div className="text-white/50 mt-1">
                {analytics?.botWinRate === 0 ? (
                  "No bot games completed yet. Win rate will appear after bot hands are played."
                ) : analytics?.botWinRate && analytics.botWinRate > 50 ? (
                  `Bots are winning ${analytics.botWinRate.toFixed(1)}% of hands. Consider adjusting difficulty.`
                ) : (
                  `Bots have a ${analytics?.botWinRate.toFixed(1)}% win rate — players are performing well.`
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
